import React from 'react';
import { Alert, Box, Grid, Typography } from 'housecall-ui';

import { TimeSlot } from '../api/api';
import { useTimeSlotsDict } from '../hooks/useTimeSlots';
import { actions } from '../state/context';
import { useOnlineBookingReducer } from '../state/useOnlineBookingReducer';

import { TimeSlotElement } from './TimeSlotElement';
import { TimeSlotsSkeleton } from './TimeSlotsSkeleton';

type Props = {
  /**
   * YYYY-MM-DD
   */
  date?: string;
  dateFrom: Date;
  dateTo: Date;
  isExactTime: boolean;
};

const isTheSameTimeslot = (firstTimeSlot: TimeSlot, nextTimeSlot?: TimeSlot) => {
  return firstTimeSlot.from === nextTimeSlot?.from && firstTimeSlot.to === nextTimeSlot?.to;
};

export const TimeSlots = ({ date, dateFrom, dateTo, isExactTime }: Props) => {
  const {
    state: {
      orgUuid,
      service,
      arrivalWindow: { time: selectedTimeSlot },
      unavailableDateError,
    },
    dispatch,
  } = useOnlineBookingReducer();
  const serviceUuid = service?.uuid ?? '';
  const {
    data: timeSlotsDict,
    isLoading,
    isFetching,
    isError,
    isFetched,
  } = useTimeSlotsDict({ orgUuid, dateFrom, dateTo, serviceUuid });

  if (isLoading || isFetching) {
    return <TimeSlotsSkeleton />;
  }

  const timeSlots = timeSlotsDict && date ? timeSlotsDict[date] : undefined;
  const handleTimeSlotClick = (timeSlotClicked: TimeSlot) => {
    dispatch(actions.selectArrivalTime(timeSlotClicked));
  };

  const timeSlotsUnavailable = isError || (isFetched && unavailableDateError && !timeSlots);

  return (
    <Box mb={1}>
      <Typography type="body1" gutterBottom>
        {isExactTime ? 'We’ll arrive at:' : 'We’ll arrive within the selected timeframe:'}
      </Typography>
      <Grid container spacing={2}>
        {timeSlotsUnavailable ? (
          <Grid item xs={12}>
            <Alert severity="error">Booking hours are unavailable, please refresh the page.</Alert>
          </Grid>
        ) : (
          timeSlots?.map((timeslot) => (
            <Grid key={timeslot.from} item md={4} xs={6}>
              <TimeSlotElement
                timeslot={isExactTime ? timeslot.from : `${timeslot.from.slice(0, -2)} - ${timeslot.to}`}
                selected={isTheSameTimeslot(timeslot, selectedTimeSlot)}
                onClick={() => {
                  handleTimeSlotClick(timeslot);
                }}
              />
            </Grid>
          ))
        )}
      </Grid>
    </Box>
  );
};
